import { createFileRoute } from '@tanstack/react-router';
import { ListPlus } from 'lucide-react';
import { useMemo } from 'react';
import { atLeast } from '@regent/shared';
import { Button, EmptyState, SkeletonRows } from '@/components/ui';
import { useHierarchyIndex } from '@/features/hierarchy/api';
import { useHierarchyDialog } from '@/features/hierarchy/dialogs';
import { LocationOverview } from '@/features/hierarchy/location-overview';
import { Page } from '@/features/shell/page';
import type { TaskScope } from '@/features/tasks/api';
import { TaskViewPage } from '@/features/tasks/task-view-page';

export const Route = createFileRoute('/w/$workspaceId/s/$spaceId')({ component: SpacePage });

function SpacePage() {
  const { spaceId } = Route.useParams();
  const index = useHierarchyIndex();
  const openDialog = useHierarchyDialog((s) => s.open);
  const space = index.space(spaceId);
  const scope = useMemo<TaskScope>(() => ({ type: 'space', id: spaceId }), [spaceId]);

  if (index.isLoading) {
    return (
      <Page title="Space">
        <SkeletonRows className="p-6" />
      </Page>
    );
  }
  if (!space) {
    return (
      <Page title="Space">
        <EmptyState
          className="h-full"
          title="Space not found"
          description="It may have been deleted, or you do not have access."
        />
      </Page>
    );
  }
  const icon = (
    <span
      className="flex size-5 items-center justify-center rounded text-[10px] font-bold text-white"
      style={{ backgroundColor: space.color }}
    >
      {space.icon ?? space.name[0]?.toUpperCase()}
    </span>
  );
  const newList = atLeast(space.access, 'edit') ? (
    <Button
      size="sm"
      variant="ghost"
      onClick={() => openDialog({ mode: 'create', kind: 'list', spaceId, folderId: null })}
    >
      <ListPlus /> New list
    </Button>
  ) : undefined;

  // A space with no lists has no tasks to show yet: guide the user to create a list.
  if (!index.lists.some((l) => l.spaceId === spaceId)) {
    return (
      <Page title={space.name} icon={icon} actions={newList}>
        <LocationOverview spaceId={spaceId} />
      </Page>
    );
  }
  return <TaskViewPage scope={scope} title={space.name} icon={icon} actions={newList} />;
}
